import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { LeadStageMaster } from '../../types';
import { leadStageMasterService } from '../../data/leadStageMasterService';
import { isSuccessResponse } from '../../../../services/enhancedApiClient';

// ============================================================
// State
// ============================================================

interface StageMasterState {
    stages: LeadStageMaster[];
    loading: boolean;
    error: string | null;
    loaded: boolean;  // Track if initial fetch is done
}

const initialState: StageMasterState = {
    stages: [],
    loading: false,
    error: null,
    loaded: false,
};

// ============================================================
// Thunks
// ============================================================

export const fetchLeadStages = createAsyncThunk(
    'stageMaster/fetchLeadStages',
    async (includeInactive: boolean = false, { rejectWithValue }) => {
        try {
            const response = await leadStageMasterService.getStages(includeInactive);
            if (isSuccessResponse(response)) {
                return response.data;
            }
            return rejectWithValue('Failed to fetch lead stages');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to fetch lead stages';
            return rejectWithValue(message);
        }
    }
);

export const createLeadStage = createAsyncThunk(
    'stageMaster/createLeadStage',
    async (data: Partial<LeadStageMaster>, { rejectWithValue }) => {
        try {
            const response = await leadStageMasterService.createStage(data);
            if (isSuccessResponse(response)) {
                return response.data;
            }
            return rejectWithValue('Failed to create lead stage');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to create lead stage';
            return rejectWithValue(message);
        }
    }
);

export const updateLeadStage = createAsyncThunk(
    'stageMaster/updateLeadStage',
    async ({ id, data }: { id: string; data: Partial<LeadStageMaster> }, { rejectWithValue }) => {
        try {
            const response = await leadStageMasterService.updateStage(id, data);
            if (isSuccessResponse(response)) {
                return response.data;
            }
            return rejectWithValue('Failed to update lead stage');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to update lead stage';
            return rejectWithValue(message);
        }
    }
);

export const reorderLeadStages = createAsyncThunk(
    'stageMaster/reorderLeadStages',
    async (stages: Array<{ id: string; stageOrder: number }>, { rejectWithValue }) => {
        try {
            const response = await leadStageMasterService.reorderStages(stages);
            if (isSuccessResponse(response)) {
                return response.data;
            }
            return rejectWithValue('Failed to reorder lead stages');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to reorder lead stages';
            return rejectWithValue(message);
        }
    }
);

export const deleteLeadStage = createAsyncThunk(
    'stageMaster/deleteLeadStage',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await leadStageMasterService.deleteStage(id);
            if (isSuccessResponse(response)) {
                return { id, ...response.data };
            }
            return rejectWithValue('Failed to delete lead stage');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to delete lead stage';
            return rejectWithValue(message);
        }
    }
);

// ============================================================
// Slice
// ============================================================

const stageMasterSlice = createSlice({
    name: 'stageMaster',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch all stages
        builder
            .addCase(fetchLeadStages.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLeadStages.fulfilled, (state, action) => {
                state.loading = false;
                state.stages = action.payload;
                state.loaded = true;
            })
            .addCase(fetchLeadStages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Create stage
        builder
            .addCase(createLeadStage.fulfilled, (state, action) => {
                state.stages.push(action.payload);
                // Re-sort by stage_order
                state.stages.sort((a, b) => a.stageOrder - b.stageOrder);
            });

        // Update stage
        builder
            .addCase(updateLeadStage.fulfilled, (state, action) => {
                const index = state.stages.findIndex(s => s.id === action.payload.id);
                if (index !== -1) {
                    state.stages[index] = action.payload;
                }
                state.stages.sort((a, b) => a.stageOrder - b.stageOrder);
            });

        // Reorder stages
        builder
            .addCase(reorderLeadStages.fulfilled, (state, action) => {
                state.stages = action.payload;
            });

        // Delete (deactivate) stage
        builder
            .addCase(deleteLeadStage.fulfilled, (state, action) => {
                state.stages = state.stages.filter(s => s.id !== action.payload.id);
            });
    },
});

export default stageMasterSlice.reducer;

// ============================================================
// Selectors
// ============================================================

export const selectAllStages = (state: { stageMaster: StageMasterState }) => state.stageMaster.stages;
export const selectActiveStages = (state: { stageMaster: StageMasterState }) =>
    state.stageMaster.stages.filter(s => s.isActive);
export const selectStagesLoading = (state: { stageMaster: StageMasterState }) => state.stageMaster.loading;
export const selectStagesLoaded = (state: { stageMaster: StageMasterState }) => state.stageMaster.loaded;
export const selectStageByKey = (key: string) => (state: { stageMaster: StageMasterState }) =>
    state.stageMaster.stages.find(s => s.key === key);
export const selectFixedStages = (state: { stageMaster: StageMasterState }) =>
    state.stageMaster.stages.filter(s => s.stageType === 'fixed');
export const selectOptionalStages = (state: { stageMaster: StageMasterState }) =>
    state.stageMaster.stages.filter(s => s.stageType === 'optional');
export const selectTerminalStages = (state: { stageMaster: StageMasterState }) =>
    state.stageMaster.stages.filter(s => s.stageType === 'terminal');
